import {
  genPageTheme,
  shapes,
  createBaseThemeOptions,
  createUnifiedTheme,
} from '@backstage/theme';
import { yellow } from '@material-ui/core/colors';
import { styled } from '@material-ui/core/styles';
import { HEADER_HEIGHT } from './layoutConstants';

export const ingkaLight = createUnifiedTheme({
  fontFamily: 'Noto IKEA',
  ...createBaseThemeOptions({
    palette: {
      type: 'light',
      background: {
        default: '#FFFFFF',
      },
      status: {
        ok: '#0A8A00',
        warning: '#FFA524',
        error: '#E00751',
        running: '#2E77D0',
        pending: '#FFDB00',
        aborted: '#929292',
      },
      bursts: {
        fontColor: '#FEFEFE',
        slackChannelText: '#DFDFDF',
        backgroundColor: {
          default: '#7C3699',
        },
        gradient: {
          linear: 'linear-gradient(-137deg, #4BB8A5 0%, #187656 100%)',
        },
      },
      primary: {
        main: '#0058A3',
      },
      banner: {
        info: '#0058A3',
        error: '#E00751',
        text: '#FFFFFF',
        link: '#111111',
      },
      border: '#DFDFDF',
      textContrast: '#111111',
      textVerySubtle: '#DFDFDF',
      textSubtle: '#767676',
      highlight: '#FFDB00',
      errorBackground: '#FFFBCC',
      warningBackground: '#F59B23',
      infoBackground: '#ebf5ff',
      errorText: '#E00751',
      infoText: '#0058A3',
      warningText: '#111111',
      linkHover: '#2196F3',
      link: '#0058A3',
      gold: yellow.A700,
      navigation: {
        background: '#FFFFFF',
        indicator: '#FFDB00',
        color: '#111111',
        selectedColor: '#111111',
        submenu: {
          background: '#F5F5F5',
        },
      },
      pinSidebarButton: {
        icon: '#111111',
        background: '#929292',
      },
      tabbar: {
        indicator: '#FFDB00',
      },
    },
  }),
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({ colors: ['#003E72', '#003E72'], shape: shapes.wave }),
    documentation: genPageTheme({
      colors: ['#003E72', '#003E72'],
      shape: shapes.wave2,
    }),
    tool: genPageTheme({ colors: ['#003E72', '#003E72'], shape: shapes.round }),
    service: genPageTheme({
      colors: ['#003E72', '#003E72'],
      shape: shapes.wave,
    }),
    website: genPageTheme({
      colors: ['#003E72', '#003E72'],
      shape: shapes.wave,
    }),
    library: genPageTheme({
      colors: ['#003E72', '#003E72'],
      shape: shapes.wave,
    }),
    other: genPageTheme({ colors: ['#003E72', '#003E72'], shape: shapes.wave }),
    app: genPageTheme({ colors: ['#003E72', '#003E72'], shape: shapes.wave }),
    apis: genPageTheme({ colors: ['#003E72', '#003E72'], shape: shapes.wave2 }),
  },
  components: {
    MuiChip: {
      styleOverrides: {
        label: {
          lineHeight: '20px',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(odd)': {
            backgroundColor: '#F5F5F5',
          },
          '&:nth-of-type(even)': {
            backgroundColor: '#FFFFFF',
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
        },
      },
    },
    BackstagePage: {
      styleOverrides: {
        root: () => ({
          paddingTop: 0,
          boxSizing: 'border-box' as const,
          // Remove padding from nested TechDocs page embedded in EntityPage (not standalone)
          '& .techdocs-reader-page': {
            paddingTop: 0,
          },
          // Remove vertical padding from parent content wrapper when TechDocs is immediate child
          '& [class*="BackstageContent-root"]:has(> .techdocs-reader-page)': {
            padding: '0 32px !important',
          },
        }),
      },
    },
    BackstageContent: {
      styleOverrides: {
        root: () => ({
          // Remove vertical padding when inside TechDocs page (standalone or embedded)
          '.techdocs-reader-page > &': {
            padding: '0 32px !important',
          },
        }),
      },
    },
    BackstageHeader: {
      styleOverrides: {
        header: () => ({
          backgroundImage: 'none',
          background: '#FFFFFF',
          boxShadow: 'none',
          borderBottom: '1px solid #F5F5F5',
          marginBottom: '12px',
          '& .MuiButtonBase-root.PluginCatalogEntityContextMenu-button svg': {
            color: '#FF0000',
          },
        }),
        title: () => ({
          color: '#000000',
        }),
        subtitle: () => ({
          color: '#000000',
        }),
        type: () => ({
          color: '#000000',
        }),
        breadcrumb: () => ({
          color: '#000000',
        }),
      },
    },
    BackstageHeaderLabel: {
      styleOverrides: {
        root: () => ({
          color: '#000000',
          '& svg': {
            color: '#000000',
          },
        }),
        label: () => ({
          color: '#000000',
        }),
        value: () => ({
          color: '#000000',
        }),
      },
    },
    PluginCatalogEntityContextMenu: {
      styleOverrides: {
        button: {
          color: '#000000',
        },
      },
    },
    BackstageSidebar: {
      styleOverrides: {
        drawer: () => ({
          top: HEADER_HEIGHT,
          zIndex: 200,
          borderRight: '1px solid #F5F5F5',
        }),
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        selected: () => ({
          '&$root': {
            borderLeft: 'solid 0px transparent',
            '&:hover $iconContainer': {
              backgroundColor: '#FFDB00',
              color: '#000000',
            },
            '& $label': {
              fontWeight: 'bold',
            },
          },
          '& $iconContainer': {
            backgroundColor: '#FFDB00',
            borderRadius: '50%',
          },
          '& $closedItemIcon': {
            backgroundColor: '#FFDB00',
            borderRadius: '50%',
            paddingRight: 0,
            '& .MuiBox-root': {
              alignItems: 'center',
            },
          },
        }),
        highlighted: () => ({
          background: 'transparent',
        }),
        highlightable: () => ({
          '&:hover': {
            '& $iconContainer': {
              backgroundColor: '#545454',
              borderRadius: '50%',
              color: '#ffffff',
            },
            backgroundColor: 'transparent',
          },
        }),
        label: () => ({
          fontWeight: 'normal',
        }),
        iconContainer: () => ({
          backgroundColor: 'transparent',
          height: '40px',
          width: '40px',
          marginRight: '8px',
          marginLeft: '16px !important',
          '& .MuiAvatar-root': {
            width: '40px',
            height: '40px',
            backgroundColor: '#000000',
          },
        }),
        secondaryAction: () => ({
          backgroundColor: 'transparent',
          '& .MuiSvgIcon-root': {
            color: '#000000',
          },
        }),
        closedItemIcon: () => ({
          height: '40px',
          width: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& .MuiAvatar-root': {
            backgroundColor: '#000000',
            height: '40px',
            width: '40px',
          },
          '& .MuiBox-root': {
            alignItems: 'center',
          },
        }),
      },
    },
    BackstageSidebarSubmenuItem: {
      styleOverrides: {
        item: () => ({
          '&:hover': {
            backgroundColor: '#DFDFDF',
          },
        }),
        textContent: () => ({
          color: '#111111',
        }),
        dropdownItem: () => ({
          '&:hover': {
            backgroundColor: '#DFDFDF',
          },
        }),
      },
    },
  },
});

export const SidebarDivider = styled('hr')(({ theme }) => ({
  height: 1,
  width: '100%',
  background: 'transparent',
  border: 'none',
  margin: theme.spacing(1.2, 0),
}));
